import { useRef, useState, type DragEvent, type ReactNode } from "react";
import { Loader2, UploadCloud } from "lucide-react";
import { cn } from "@/lib/utils";
import { focusRing } from "@/components/Onboarding/ui/buttons";

interface DropzoneProps {
  onFile: (file: File) => void;
  accept?: string;
  title: string;
  body: string;
  /** Column pills shown under the copy. */
  columns?: string[];
  reading?: boolean;
  error?: string | null;
  /** Links under the zone (template download, sample). */
  footer?: ReactNode;
  disabled?: boolean;
}

/**
 * Drag-and-drop file target with a hidden input behind it (native events, no
 * dependency). States: idle, drag-over, reading, error. Tokens only.
 */
export function Dropzone({
  onFile,
  accept = ".csv,text/csv",
  title,
  body,
  columns = [],
  reading = false,
  error,
  footer,
  disabled = false,
}: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  const pick = (list: FileList | null) => {
    const file = list?.[0];
    if (file) onFile(file);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setOver(false);
    if (disabled || reading) return;
    pick(e.dataTransfer.files);
  };

  return (
    <div className="flex flex-col gap-3">
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-busy={reading || undefined}
        data-testid="onboarding-dropzone"
        onClick={() => !disabled && !reading && inputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !disabled && !reading) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled && !reading) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        className={cn(
          "flex cursor-pointer flex-col items-center gap-3 rounded-g-xl border-2 border-dashed px-6 py-10 text-center transition-colors duration-150 ease-out",
          over
            ? "border-g-primary bg-g-primary-wash"
            : error
              ? "border-g-danger bg-g-danger-tint"
              : "border-g-line-strong bg-g-bg-input hover:border-g-primary-muted hover:bg-g-primary-subtle",
          (disabled || reading) && "cursor-not-allowed opacity-70",
          focusRing,
        )}
      >
        <span
          className="flex h-12 w-12 items-center justify-center rounded-g-pill bg-g-primary-tint text-g-primary"
          aria-hidden="true"
        >
          {reading ? <Loader2 className="h-6 w-6 animate-spin" /> : <UploadCloud className="h-6 w-6" />}
        </span>
        <span className="text-[16px] font-bold text-g-ink-900">{title}</span>
        <span className="text-[13.5px] text-g-ink-500">{body}</span>
        {columns.length > 0 ? (
          <span className="mt-1 flex flex-wrap justify-center gap-1.5">
            {columns.map((column) => (
              <span
                key={column}
                className="rounded-g-pill border border-g-line bg-g-bg-card px-2.5 py-1 text-[11.5px] font-semibold text-g-ink-600"
              >
                {column}
              </span>
            ))}
          </span>
        ) : null}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="sr-only"
          data-testid="onboarding-dropzone-input"
          onChange={(e) => {
            pick(e.currentTarget.files);
            e.currentTarget.value = "";
          }}
          disabled={disabled}
        />
      </div>
      {error ? (
        <p className="text-[13px] font-medium text-g-danger" role="alert">
          {error}
        </p>
      ) : null}
      {footer ? <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px]">{footer}</div> : null}
    </div>
  );
}
